"use client";

import { openModal } from "action/modal/openModal";
import { IProductLineActualResponse } from "api/interfaces/get-page-details/line-page.type";
import { IPageContent } from "api/interfaces/static-content.type";
import PAButton from "component/design-system/buttons/PAButton";
import { MAKE_MODEL_YEAR } from "component/vehicle-form/constants";
import matchMediaConst from "constant/matchmedia";
import { useCustomScreenSize } from "hoc/useScreenSize";
import { RootState } from "interfaces/generics.type";
import PageContainer from "modules/components/PageContainer";
import useNonInitialEffect from "modules/lib/hooks/useNonInitialEffect";
import RatingUI from "modules/third-party/sitejabber/RatingUI";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import qs from "query-string";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import HideIf from "util/HideIf";
import { pick } from "util/lodash/pick";
import { CATEGORY_PAGE_TYPES, PAGE_TYPES } from "util/product/product-constants";
import { refreshData } from "util/refreshData";
import { getHrefAndPath } from "util/url/getHrefAndPath";
import { validateVehicle } from "util/vehicle/validate-vehicle";
import { validQueryParams } from "../category/constants/constants";
import ProductPageHeader from "./header/ProductPageHeader";
import LinePageSection from "./line-page/LinePageSection";
import ProductLineMetaHead from "./meta/ProductLineMetaHead";
import Breadcrumbs from "./header/Breadcrumbs";
import styles from "./product.module.scss";
const SezzleWidget = dynamic(() => import(/* webpackChunkName: "sezzle-widget" */"component/design-system/bnpl-widget/sezzleWidget"));

let CSR_DONE = false;
interface IProps {
  actualResponse: IProductLineActualResponse;
  staticContent: IPageContent;
  pageType: string;
  isSSR: boolean;
}
function ProductLineContainer(props: IProps) {
  const {
    actualResponse, details = {}, pageType, productQuestions = [], productReviews = [], staticContent, lineProducts, make, model, isSSR,
  } = props;

  const {
    productsAggregation,
    fitmentOptionsDetails,
    breadCrumbsV2,
    minimumPrice,
    maximumPrice,
    tiersWithUrls,
  } = actualResponse;

  const dispatch = useDispatch();
  const { pageDetails = {}, breadcrumbs } = staticContent;
  const { manufacturer } = pageDetails;

  const { selectedVehicle, isLoading: loadingGarage } = useSelector((state: RootState) => state.garage);
  const { previousPageType } = useSelector((store: RootState) => store.utils);
  const router = useRouter();
  const { pathname } = router.query;
  const isMobile = useCustomScreenSize({
    maxWidth: matchMediaConst.XS_MAX,
  });
  const [updatedProducts, setUpdatedProducts] = useState(productsAggregation);
  const { t } = useTranslation("common");

  useEffect(() => {
    router.events.on("routeChangeComplete", vehicleSelector);

    return () => {
      router.events.off("routeChangeComplete", vehicleSelector);
    };
  }, []);

  useEffect(() => {
    if (!loadingGarage) {
      vehicleSelector();
    }
  }, [loadingGarage]);

  useEffect(() => {
    if (loadingGarage) {
      return;
    }
    if (!CSR_DONE && isSSR) {
      CSR_DONE = true;
      // client side data refesh single time, for SSR page only
      // as SSR page might be cached and user have stale prices or product info
      refreshData({
        pathname,
        isMobile,
        selectedVehicle,
      })
        .then(({ keysToUpdate }) => {
          setUpdatedProducts(keysToUpdate?.productsAggregation || productsAggregation);
        });
    }
  }, [loadingGarage]);

  function vehicleSelector() {
    if (loadingGarage) {
      return;
    }
    if (pageType === PAGE_TYPES.MAKE_MODEL_PRODUCT_LINE_PAGE) {
      const dMake = make || "";
      const dModel = model || "";
      dispatch(openModal("vehicleSelector", {
        title: `${t("Select Year for")} ${dMake} ${dModel}?`,
        defaultMake: make,
        defaultModel: model,
        selectionOrder: MAKE_MODEL_YEAR,
      }));
    } else if (!validateVehicle(selectedVehicle)) {
      dispatch(openModal("vehicleSelector"));
    }
  }

  useNonInitialEffect(() => {
    const allQuery = pick(process.browser ? qs.parse(location.search) : router.query, validQueryParams);
    const queryParams = {
      ...allQuery,
      make: undefined,
      model: undefined,
      year: undefined,
      engine: undefined,
      pageNumber: undefined, // reset page number, no change of vehicle
    };
    const queryString = qs.stringify(queryParams);

    const { href, asPath } = getHrefAndPath({ queryString, href: router.query.pathname, asPath: router.query.pathname });
    router.replace(href, asPath)
      .then(() => window.scrollTo(0, 0));
  }, [selectedVehicle]);

  const propsToPass = {
    make,
    model,
    staticContent,
    productReviews,
    productQuestions,
    details,
    breadCrumbsV2,
    lineProducts,
    maximumPrice,
    minimumPrice,
    components: {
      RatingUI,
    },
    tiersWithUrls,
  };

  return (
    <>
      <Breadcrumbs
        {...{
          breadCrumbsV2,
          breadcrumbs,
          staticContent,
        }}
        showBackToResult={CATEGORY_PAGE_TYPES.includes(previousPageType)}
        className={`${styles.compact_breadcrumb} ${styles.product_breadcrumb}`}
      />
      <div style={{ fontSize: "calc(1.5 * var(--safari-root-font, 10px))" }} className="test-product-container">
        <ProductLineMetaHead {...{
          pageType, productQuestions, productReviews, staticContent, productsAggregation: updatedProducts, maximumPrice, minimumPrice, locale: router.locale,
        }}
        />

        <PageContainer className="d-flex flex-column">
          <HideIf show={Object.keys(staticContent).length}>
            <ProductPageHeader
              {...{
                breadCrumbsV2,
                breadcrumbs,
                pageType,
                productQuestions,
                productReviews,
                staticContent,
                manufacturer,
                maximumPrice,
                minimumPrice,
                make,
                model,
              }}
              showBreadCrumbs
              showPrice
              displayInOneLine
              desktopHeaderRightSection={(
                <>
                  <div className="hidden-sm hidden-xs">
                    <SezzleWidget price={0} />
                    <div className={styles.header_right_section}>
                      <div className="row-no-padding-xs">
                        <SezzleWidget className="show p-b-20 p-t-20" />
                        <PAButton
                          style={{ width: 250, float: "right" }}
                          onClick={() => router.push(`${router.query.pathname}#PRODUCTS`)}
                        >
                          {t("Product Options")}
                        </PAButton>
                      </div>
                    </div>
                  </div>
                </>
              )}
            />
          </HideIf>

          <LinePageSection
            {...propsToPass}
            {...{
              productsAggregation: updatedProducts,
              pageType,
              fitmentOptionsDetails,
            }}
          />
        </PageContainer>
      </div>
    </>
  );
}

export default ProductLineContainer;
